import axios from "axios";
import axiosInstance from "./axiosInstance";

// Plain axios — no auth header; these are public endpoints, the same arrangement
// signApi uses. Going through axiosInstance would attach a bearer token that a
// stranger does not have and, on a 401, drag them through the session-refresh path.
const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

// Whose link is this? Name only — nothing else about the referrer is public.
export const getReferrerByCode = (code) =>
    axios.get(`${API_BASE}/ReferralLink/ByCode?code=${encodeURIComponent(code)}`);

// The details go in the BODY, never the query string: a name and a phone number in
// a URL end up in server logs and browser history.
export const submitReferral = ({ code, name, phone, email, location, note }) =>
    axios.post(`${API_BASE}/ReferralLink/Submit`, { code, name, phone, email, location, note });

// Making a referrer from inside the sale. Authenticated, so it goes through
// axiosInstance rather than the plain axios the public pair above uses.
// (user 2026-10-19)
export const quickCreateReferral = (payload) =>
    axiosInstance.post('/QuickReferral/Create', payload);

// The referrers this person introduced — theirs by UplineId. A different question from
// My Referral Report, which is built from sales and so cannot show a referrer who has
// not produced one yet. Both caller-scoped, with no id to pass. (user 2026-10-20)
export const getMyReferrers = () =>
    axiosInstance.get('/QuickReferral/Mine');
export const updateMyReferrer = (payload) =>
    axiosInstance.put('/QuickReferral/Update', payload);
// Active <-> Archived, the same flip the user list performs. Scoped to referrers the
// caller introduced. (user 2026-10-21: "edit, archieve by sales person")
export const archiveMyReferrer = (payload) =>
    axiosInstance.put('/QuickReferral/Archive', payload);
// The sales one of my referrers brought in. No fee column by design — the fee has one
// derivation (GetReferralFees) and a second one here would drift from it.
// (user 2026-10-21: "sales history")
export const getReferrerSales = (userId) =>
    axiosInstance.get(`/QuickReferral/Sales?userId=${encodeURIComponent(userId)}`);

// A referrer's own /r/{code} share link, for the agent to forward. Minted on first ask
// and unchanging afterwards — it is an identity, not a token, so asking twice gives the
// same link. (user 2026-11-01: "so agent can share the link to them")
export const getReferrerShareLink = (userId) =>
    axiosInstance.post(`/QuickReferral/ShareLink?userId=${encodeURIComponent(userId)}`);

// Customers who could become referrers: they have a phone, and that phone does not
// already belong to a user. Super Admin / Accountant only — the server enforces it.
export const getCustomerCandidates = () =>
    axiosInstance.get('/QuickReferral/CustomerCandidates');

// Creates them, in capped batches. Returns { created, remaining } so the dialog can keep
// going until nothing is left.
export const createReferrersFromCustomers = (limit = 200) =>
    axiosInstance.post('/QuickReferral/CreateReferrersFromCustomers', { limit });

// A one-time link that lets a referrer set their own password and sign in. Returned to
// US rather than sent anywhere: most referrers have no email on file, so the sales person
// who introduced them passes it on by WhatsApp. Seven days, single use, and it retires any
// earlier unused link for that account. (user 2026-10-31: "give them login")
export const getReferrerLoginLink = (userId) =>
    axiosInstance.post(`/QuickReferral/LoginLink?userId=${encodeURIComponent(userId)}`);

// Referral Leads — every introduction a referrer sent in, scoped by the SERVER: Super
// Admin and Accountant see all, everyone else sees the leads of referrers they
// introduced. No id to pass, for the same reason as the rest of this file: a caller
// cannot widen what they are allowed to read by changing a number in a URL.
// (user 2026-10-31: "have another page is Referral Leads")
export const getReferralLeads = () =>
    axiosInstance.get('/ReferralLink/Leads');

// Records the sale a lead became. Called once, right after the sale is created, from
// the create-sales page the lead opened. Commission is untouched — the 2% rides on
// Sales.ReferralUserId, which the sale itself carries.
export const attachSaleToLead = ({ referralEnquiryId, salesId }) =>
    axiosInstance.post('/ReferralLink/AttachSale', { referralEnquiryId, salesId });

// The referrer's IC photocopy. NOT a /uploads URL on purpose: that path is served
// by static middleware with no auth, and a government ID should not be readable by
// anyone holding the link. It comes back through an authenticated endpoint instead,
// which is why fetching it means a blob rather than an <img src>.
// (user 2026-10-21: "referral need to add referral IC photo copy.")
export const uploadReferrerIcPhoto = (userId, file) => {
    const form = new FormData();
    form.append('file', file);
    return axiosInstance.post(`/QuickReferral/UploadIcPhoto?userId=${encodeURIComponent(userId)}`, form,
        { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const getReferrerIcPhoto = (userId) =>
    axiosInstance.get(`/QuickReferral/IcPhoto?userId=${encodeURIComponent(userId)}`, { responseType: 'blob' });
export const deleteReferrerIcPhoto = (userId) =>
    axiosInstance.delete(`/QuickReferral/DeleteIcPhoto?userId=${encodeURIComponent(userId)}`);
